import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { OWNER_ROLE_ID } from "@shared/protocol";
import type { Message } from "@shared/types";
import { AttachmentView } from "@/components/AttachmentView";
import { EmojiPicker } from "@/components/EmojiPicker";
import { plainPreview, renderRichText } from "@/lib/richText";
import { can, colorOf, messageById, roleOf, useApp, userById } from "@/state/store";
import { C, FONT_MONO, formatStamp, hexA, initialOf } from "@/theme";

const ACTION_BUTTON: CSSProperties = {
  width: 28,
  height: 28,
  display: "grid",
  placeItems: "center",
  border: 0,
  borderRadius: 7,
  cursor: "pointer",
  background: "transparent",
  color: C.textMuted,
};

/** Folga entre o botao de reagir e o seletor de emoji. */
const PICKER_GAP = 8;

/** Respiro minimo entre o seletor e a borda da janela. */
const PICKER_EDGE = 12;

/**
 * Posicao fixa do seletor de emoji. Ele e desenhado num portal porque a lista de
 * mensagens rola com `overflow`, e um popup grande dentro dela seria recortado.
 */
type PickerAnchor = {
  right: number;
  top: number | null;
  bottom: number | null;
  maxHeight: number;
};

function ReplyGlyph(): ReactElement {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.5 7 5 11.5 9.5 16" />
      <path d="M5 11.5h9a5 5 0 0 1 5 5V19" />
    </svg>
  );
}

function EmojiGlyph(): ReactElement {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.5 14.2c.9 1.2 2.1 1.9 3.5 1.9s2.6-.7 3.5-1.9" />
      <path d="M9.3 9.6h.01 M14.7 9.6h.01" />
    </svg>
  );
}

function PencilGlyph(): ReactElement {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 19.5h4L19 9a2.5 2.5 0 0 0-3.5-3.5L5 16v3.5z" />
      <path d="M14.5 6.5 17.5 9.5" />
    </svg>
  );
}

function TrashGlyph(): ReactElement {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 7h15" />
      <path d="M9.5 7V4.9h5V7" />
      <path d="M6.8 7 7.8 19.2h8.4L17.2 7" />
      <path d="M10.5 10.4v6 M13.5 10.4v6" />
    </svg>
  );
}

export type MessageItemProps = {
  message: Message;
  /** Primeira mensagem do bloco do autor: mostra avatar, nome e hora. */
  showHeader: boolean;
  /** Realce temporario ao pular de um resultado de busca. */
  highlight?: boolean;
};

export function MessageItem({
  message,
  showHeader,
  highlight = false,
}: MessageItemProps): ReactElement {
  const { state, actions } = useApp();
  const [hover, setHover] = useState(false);
  const [editDraft, setEditDraft] = useState(message.body);
  const [anchor, setAnchor] = useState<PickerAnchor | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const editRef = useRef<HTMLTextAreaElement | null>(null);
  const reactRef = useRef<HTMLButtonElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const me = state.me;
  const author = userById(state, message.authorId);
  const authorName = author?.name ?? message.authorId;
  const authorColor = colorOf(state, message.authorId);
  const role = roleOf(state, message.authorId);
  const editing = state.editing?.id === message.id;
  const pickerOpen = state.reactionPickerFor === message.id;
  const mine = Boolean(me && me.id === message.authorId);
  const mentionsMe = Boolean(me && message.mentions.includes(me.id));
  const canDelete = mine || can(state, "expulsar");
  // Responder termina em envio: sem a permissao de mensagens seria beco sem saida.
  const canReply = can(state, "mensagens");

  const replied = useMemo(() => {
    const target = message.replyToId;
    if (!target) return null;
    return messageById(state, target) ?? null;
  }, [message.replyToId, state]);

  const body = useMemo(
    () => (message.deleted ? [] : renderRichText(message.body, state.users, message.id)),
    [message.deleted, message.body, message.id, state.users],
  );

  useEffect(() => {
    if (!editing) return;
    setEditDraft(message.body);
    const field = editRef.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editing, message.body]);

  // Mede o botao e decide se o seletor abre para cima ou para baixo.
  useLayoutEffect(() => {
    if (!pickerOpen) {
      setAnchor(null);
      return;
    }
    const place = () => {
      const button = reactRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const right = Math.max(PICKER_EDGE, window.innerWidth - rect.right);
      const above = rect.top - PICKER_GAP - PICKER_EDGE;
      const below = window.innerHeight - rect.bottom - PICKER_GAP - PICKER_EDGE;
      setAnchor(
        above >= below
          ? {
              right,
              top: null,
              bottom: window.innerHeight - rect.top + PICKER_GAP,
              maxHeight: Math.max(180, above),
            }
          : {
              right,
              top: rect.bottom + PICKER_GAP,
              bottom: null,
              maxHeight: Math.max(180, below),
            },
      );
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [pickerOpen]);

  // Fecha o seletor de reacao ao clicar em qualquer outro lugar. O seletor vive
  // num portal, entao ele precisa ser testado a parte da linha da mensagem.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      const row = rowRef.current;
      if (row && row.contains(event.target)) return;
      const picker = pickerRef.current;
      if (picker && picker.contains(event.target)) return;
      actions.openReactionPicker(null);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [pickerOpen, actions]);

  const onEditKey = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        actions.startEditing(null);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        actions.submitEdit(editDraft.trim());
      }
    },
    [actions, editDraft],
  );

  const confirmDelete = useCallback(() => {
    if (!window.confirm("Apagar esta mensagem? Ela fica como “Mensagem apagada”.")) return;
    actions.deleteMessage(message.id);
  }, [actions, message.id]);

  const pickReaction = useCallback(
    (emoji: string) => {
      // `toggleReaction` ja fecha o seletor no store.
      actions.toggleReaction(message.id, emoji);
    },
    [actions, message.id],
  );

  const closePicker = useCallback(() => actions.openReactionPicker(null), [actions]);

  const showActions = (hover || pickerOpen) && !message.deleted && !editing;

  return (
    <div
      ref={rowRef}
      data-msg={message.id}
      className="msg-row"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: mentionsMe ? "var(--mpy, 6px) 8px var(--mpy, 6px) 6px" : "var(--mpy, 6px) 8px",
        borderLeft: mentionsMe ? "2px solid var(--ac, #1F4FD8)" : undefined,
        borderRadius: 8,
        background: highlight ? "var(--acs, rgba(46,107,255,.14))" : undefined,
        transition: "background 220ms ease",
      }}
    >
      {replied && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            marginTop: 6,
            paddingLeft: 17,
            fontSize: 12.5,
            color: C.textDim,
            minWidth: 0,
          }}
        >
          <span
            style={{
              width: 20,
              height: 9,
              flex: "0 0 auto",
              borderLeft: `1.5px solid ${C.track}`,
              borderTop: `1.5px solid ${C.track}`,
              borderTopLeftRadius: 7,
              alignSelf: "flex-end",
            }}
          />
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: colorOf(state, replied.authorId),
              whiteSpace: "nowrap",
            }}
          >
            {userById(state, replied.authorId)?.name ?? replied.authorId}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {replied.deleted
              ? "Mensagem apagada"
              : plainPreview(replied.body, 90) ||
                (replied.attachments.length > 0 ? "Anexo" : "")}
          </span>
        </div>
      )}

      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => actions.openProfile(message.authorId)}
            title={`Perfil de ${authorName}`}
            style={{
              width: 34,
              height: 34,
              flex: "0 0 auto",
              padding: 0,
              border: 0,
              borderRadius: "50%",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              fontSize: 14,
              fontWeight: 600,
              background: author?.avatarUrl ? C.lineSoft : hexA(authorColor, 0.18),
              color: authorColor,
            }}
          >
            {author?.avatarUrl ? (
              <img
                src={author.avatarUrl}
                alt=""
                style={{ width: 34, height: 34, objectFit: "cover", display: "block" }}
              />
            ) : (
              initialOf(authorName)
            )}
          </button>
          <button
            type="button"
            onClick={() => actions.openProfile(message.authorId)}
            style={{
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontSize: 14.5,
              fontWeight: 600,
              color: role?.id === OWNER_ROLE_ID ? "var(--ac, #1F4FD8)" : C.textStrong,
            }}
          >
            {authorName}
          </button>
          <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: C.textLabelDim }}>
            {formatStamp(message.createdAt)}
          </span>
        </div>
      )}

      {message.deleted ? (
        <div
          style={{
            paddingLeft: 46,
            marginTop: 2,
            fontSize: 13.5,
            fontStyle: "italic",
            lineHeight: 1.55,
            color: C.textLabelDim,
          }}
        >
          Mensagem apagada
        </div>
      ) : editing ? (
        <div style={{ paddingLeft: 46, marginTop: 4 }}>
          <textarea
            ref={editRef}
            value={editDraft}
            onChange={event => setEditDraft(event.target.value)}
            onKeyDown={onEditKey}
            rows={2}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 10px",
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 10,
              outline: 0,
              resize: "vertical",
              background: C.raised,
              color: C.text,
              fontSize: 14.5,
              lineHeight: 1.5,
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 9,
                letterSpacing: "0.1em",
                color: C.textGhost,
              }}
            >
              ENTER SALVA · ESC CANCELA
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="hov-bg-24"
              onClick={() => actions.startEditing(null)}
              style={{
                padding: "6px 12px",
                border: `1px solid ${C.lineStrong}`,
                borderRadius: 8,
                cursor: "pointer",
                background: "transparent",
                color: C.textSoft,
                fontSize: 12.5,
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="hov-bright-108"
              onClick={() => actions.submitEdit(editDraft.trim())}
              style={{
                padding: "6px 14px",
                border: 0,
                borderRadius: 8,
                cursor: "pointer",
                background: "var(--ac, #1F4FD8)",
                color: C.ink,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              Salvar
            </button>
          </div>
        </div>
      ) : (
        message.body.length > 0 && (
          <div
            style={{
              paddingLeft: 46,
              marginTop: 2,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: C.textBody,
              overflowWrap: "break-word",
            }}
          >
            {body}
            {Boolean(message.editedAt) && (
              <span style={{ marginLeft: 6, fontSize: 10.5, color: C.textLabelDim }}>(editada)</span>
            )}
          </div>
        )
      )}

      {!message.deleted && message.attachments.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            margin: "8px 0 4px 46px",
            maxWidth: 420,
          }}
        >
          {message.attachments.map(attachment => (
            <AttachmentView key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}

      {!message.deleted && message.reactions.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 5,
            margin: "6px 0 2px 46px",
          }}
        >
          {message.reactions
            .filter(reaction => reaction.userIds.length > 0)
            .map(reaction => {
              const isMine = Boolean(me && reaction.userIds.includes(me.id));
              const who = reaction.userIds
                .map(id => userById(state, id)?.name)
                .filter((name): name is string => Boolean(name))
                .join(", ");
              return (
                <button
                  key={reaction.emoji}
                  type="button"
                  className="hov-bg-24"
                  title={who}
                  onClick={() => actions.toggleReaction(message.id, reaction.emoji)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "2px 9px",
                    border: isMine ? "1px solid var(--ac, #1F4FD8)" : `1px solid ${C.lineStrong}`,
                    borderRadius: 999,
                    cursor: "pointer",
                    background: isMine ? "var(--acs, rgba(46,107,255,.14))" : C.raised,
                    color: isMine ? "var(--ac, #1F4FD8)" : C.textSoft,
                    fontSize: 12.5,
                    lineHeight: "20px",
                  }}
                >
                  <span style={{ fontSize: 13.5 }}>{reaction.emoji}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 11 }}>
                    {reaction.userIds.length}
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {showActions && (
        <div
          style={{
            position: "absolute",
            top: -12,
            right: 10,
            display: "flex",
            gap: 2,
            padding: 2,
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 9,
            background: C.raised,
            boxShadow: "0 8px 20px rgba(0,0,0,.38)",
            zIndex: 4,
          }}
        >
          {canReply && (
            <button
              type="button"
              className="hov-bg-24"
              title="Responder"
              aria-label="Responder"
              onClick={() => actions.setReplyTo(message)}
              style={ACTION_BUTTON}
            >
              <ReplyGlyph />
            </button>
          )}
          <button
            ref={reactRef}
            type="button"
            className="hov-bg-24"
            title="Reagir"
            aria-label="Reagir"
            onClick={() => actions.openReactionPicker(pickerOpen ? null : message.id)}
            style={{
              ...ACTION_BUTTON,
              background: pickerOpen ? C.hoverStrong : "transparent",
              color: pickerOpen ? C.textStrong : C.textMuted,
            }}
          >
            <EmojiGlyph />
          </button>
          {mine && (
            <button
              type="button"
              className="hov-bg-24"
              title="Editar"
              aria-label="Editar"
              onClick={() => actions.startEditing(message)}
              style={ACTION_BUTTON}
            >
              <PencilGlyph />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="hov-red"
              title="Apagar"
              aria-label="Apagar"
              onClick={confirmDelete}
              style={ACTION_BUTTON}
            >
              <TrashGlyph />
            </button>
          )}
        </div>
      )}

      {pickerOpen &&
        anchor !== null &&
        createPortal(
          <div
            ref={pickerRef}
            style={{
              position: "fixed",
              right: anchor.right,
              top: anchor.top ?? undefined,
              bottom: anchor.bottom ?? undefined,
              maxHeight: anchor.maxHeight,
              overflow: "auto",
              zIndex: 60,
              animation: "pop 120ms ease",
            }}
          >
            <EmojiPicker onPick={pickReaction} onClose={closePicker} />
          </div>,
          document.body,
        )}
    </div>
  );
}
